import React, { useState, useEffect, useCallback, useRef } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Tooltip,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  User,
  Mail,
  Phone,
  Building2,
  Clock,
  Users,
  MoreVertical,
  Circle,
  MessageSquare,
  CheckCircle,
  FileText,
  ArrowRight,
  Globe,
} from "lucide-react";
import { useTheme } from "@mui/material/styles";

import { Badge } from "../../components/Badge";
import { useAuth } from "../../components/contexts/AuthContext";
import { getData } from "../../components/hooks/getData";
import apiClient from "../../api-client";
import SuccessPopup from "../../components/SuccessPopup";
import {
  DropdownMenuContent,
  DropdownMenuItem,
} from "../../components/DropdownMenu";
import { DataTable } from "../../components/DataTable";
import { UnifiedModal } from "../../components/UnifiedModal";
import { CustomSelector } from "../../components/CustomSelector";
import { Button } from "../../components/Button";
import { AgencyIcon } from "../../components/icons/AgencyIcon";

// === APIS DEL BACKEND ===

/**
 * Hook para obtener todos los leads con paginación del servidor
 * Soporta filtros: search, status, page, page_size
 */
const useLeads = (
  interceptorReady,
  shouldFetch,
  filters = {},
  refreshDeps = []
) => {
  const queryParams = new URLSearchParams();

  if (filters.search) {
    queryParams.append("search", filters.search);
  }

  if (filters.status && filters.status !== "all") {
    queryParams.append("status", filters.status);
  }

  if (filters.page) {
    queryParams.append("page", filters.page);
  }

  if (filters.page_size) {
    queryParams.append("page_size", filters.page_size);
  }

  const endpoint = `/leadmagnet/leads/${queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;

  return getData(endpoint, interceptorReady && shouldFetch, refreshDeps);
};

// === CONFIGURACIONES ===

const statusConfig = {
  new: {
    label: "Nuevo",
    color: "red",
  },
  contacted: {
    label: "Contactado",
    color: "yellow",
  },
  converted: {
    label: "Convertido",
    color: "green",
  },
};

// Configuración de métodos de captación del lead
const captacionConfig = {
  marketing_plan_ia: {
    label: "MarketingPlanIA",
    color: "purple",
  },
  otros: {
    label: "Otros",
    color: "blue",
  },
};

export function Leads() {
  const { user: currentUser, interceptorReady } = useAuth();
  const navigate = useNavigate();
  const {
    setPageContext,
    leadsRefreshKey,
  } = useOutletContext();
  const theme = useTheme();

  // Estados locales
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const searchDebounceTimerRef = useRef(null);
  const lastSearchRef = useRef("");
  const [searchRefreshKey, setSearchRefreshKey] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState("all"); // Estado seleccionado
  const [refresh, setRefresh] = useState(0);

  // Estados para paginación de tabla (del servidor)
  const [currentPage, setCurrentPage] = useState(0); // TablePagination usa índice base 0
  const [pageSize, setPageSize] = useState(25);
  const [allLeads, setAllLeads] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // Estados para menú desplegable y popup
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedLeadForMenu, setSelectedLeadForMenu] = useState(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [isPopupError, setIsPopupError] = useState(false);
  const [isProcessing, setIsProcessing] = useState(null);

  // Estados para modal de asignar agencia
  const [isAgencyModalOpen, setIsAgencyModalOpen] = useState(false);
  const [selectedAgencyId, setSelectedAgencyId] = useState(null);
  const [isAssigningAgency, setIsAssigningAgency] = useState(false);
  const [leadForAgencyModal, setLeadForAgencyModal] = useState(null);

  const resetPaginationState = useCallback(() => {
    setCurrentPage(0);
    setAllLeads([]);
    setTotalCount(0);
  }, []);

  // Debounce para el buscador inline
  useEffect(() => {
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    searchDebounceTimerRef.current = setTimeout(() => {
      const trimmed = searchInput.trim();

      // CASO 1: transición búsqueda → vacío (forzar reset)
      if (trimmed === '' && lastSearchRef.current !== '') {
        lastSearchRef.current = '';
        setSearchTerm('');
        setSearchRefreshKey(prev => prev + 1);
        return;
      }

      // CASO 2: búsqueda normal (mínimo 2 caracteres)
      if (trimmed.length >= 2 && trimmed !== lastSearchRef.current) {
        lastSearchRef.current = trimmed;
        setSearchTerm(trimmed);
      }
    }, 300);

    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, [searchInput]);

  // Construir filtros para el backend (con paginación)
  const filters = React.useMemo(() => {
    return {
      search: searchTerm,
      status: selectedStatus,
      page: currentPage + 1, // Backend espera páginas base 1
      page_size: pageSize,
    };
  }, [searchTerm, selectedStatus, currentPage, pageSize]);

  // Hook para obtener leads del backend
  const { data: backendLeads, isLoading: isLoadingLeads } = useLeads(
    interceptorReady,
    !!currentUser,
    filters,
    [refresh, searchTerm, selectedStatus, leadsRefreshKey, currentPage, pageSize, searchRefreshKey]
  );

  // Hook para obtener agencias (solo para superadmin)
  const { data: agenciesData } = getData(
    "agencies/",
    interceptorReady && currentUser?.role === "superadmin",
    []
  );

  // Opciones de agencias para el selector
  const agencyOptions = React.useMemo(() => {
    const noAgencyOption = { value: null, label: "Sin agencia" };
    if (!agenciesData) return [noAgencyOption];
    const list = Array.isArray(agenciesData) ? agenciesData : agenciesData.results || [];
    const agencies = list.map((agency) => ({
      value: agency.id,
      label: agency.name,
    }));
    return [noAgencyOption, ...agencies];
  }, [agenciesData]);

  // Efecto para manejar los leads del backend
  useEffect(() => {
    if (backendLeads) {
      if (backendLeads.results) {
        // Backend devuelve paginación: { count, next, previous, results }
        setAllLeads(backendLeads.results);
        setTotalCount(backendLeads.count || 0);
      } else if (Array.isArray(backendLeads)) {
        // Fallback para compatibilidad con respuesta sin paginación
        setAllLeads(backendLeads);
        setTotalCount(backendLeads.length);
      } else {
        setAllLeads([]);
        setTotalCount(0);
      }
    }
  }, [backendLeads]);

  // Efecto para resetear paginación cuando cambian los filtros
  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm, selectedStatus]);

  // Funciones para manejar cambios de página y tamaño de página
  const handleChangePage = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (nextPageSize) => {
    setPageSize(nextPageSize);
    setCurrentPage(0); // Resetear a la primera página
  };

  const getHeaderInfo = () => {
    switch (currentUser.role) {
      case "superadmin":
        return {
          title: "Gestión de Leads",
          subtitle: "Supervisa todos los leads capturados",
        };
      case "agency_admin":
        return {
          title: "Leads",
          subtitle: "Gestiona los leads de tu agencia",
        };
      default:
        return {
          title: "Leads",
          subtitle: "Lista de leads",
        };
    }
  };

  const headerInfo = getHeaderInfo();

  // Función para cambiar el filtro de estado
  const handleStatusChange = (status) => {
    setSelectedStatus(status);
  };

  // Funciones para manejar el menú desplegable
  const handleMenuOpen = (event, lead) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedLeadForMenu(lead);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedLeadForMenu(null);
  };

  const menuOpen = Boolean(menuAnchorEl);

  // Función para actualizar el estado del lead
  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      setIsProcessing(leadId);
      await apiClient.patch(`/leadmagnet/leads/${leadId}/`, {
        status: newStatus,
      });
      // Actualizar la lista local
      setAllLeads((prevLeads) =>
        prevLeads.map((lead) =>
          lead.id === leadId ? { ...lead, status: newStatus } : lead
        )
      );
      // Mostrar popup de éxito
      const statusLabels = {
        new: "Nuevo",
        contacted: "Contactado",
        converted: "Convertido",
      };
      setPopupMessage(`Lead marcado como ${statusLabels[newStatus]}`);
      setIsPopupError(false);
      setShowSuccessPopup(true);
    } catch (error) {
      console.error("Error actualizando estado del lead:", error);
      setPopupMessage(
        error.response?.data?.message || "No se pudo actualizar el estado del lead"
      );
      setIsPopupError(true);
      setShowSuccessPopup(true);
    } finally {
      setIsProcessing(null);
      handleMenuClose();
    }
  };

  // Función para abrir el modal de asignar agencia
  const handleOpenAgencyModal = () => {
    // Guardar el lead antes de cerrar el menú
    setLeadForAgencyModal(selectedLeadForMenu);
    setSelectedAgencyId(selectedLeadForMenu?.agency_id || null);
    setIsAgencyModalOpen(true);
    handleMenuClose();
  };

  // Función para asignar agencia al lead
  const handleAssignAgency = async () => {
    if (!leadForAgencyModal || selectedAgencyId === undefined) return;

    try {
      setIsAssigningAgency(true);
      await apiClient.patch(`/leadmagnet/leads/${leadForAgencyModal.id}/`, {
        agency: selectedAgencyId,
      });

      // Actualizar la lista local
      setAllLeads((prevLeads) =>
        prevLeads.map((lead) =>
          lead.id === leadForAgencyModal.id
            ? { ...lead, agency_id: selectedAgencyId, agency_name: selectedAgencyId ? agencyOptions.find(a => a.value === selectedAgencyId)?.label : null }
            : lead
        )
      );

      // Mostrar popup de éxito
      const agencyName = selectedAgencyId
        ? agencyOptions.find(a => a.value === selectedAgencyId)?.label || "Agencia"
        : "Sin agencia";
      setPopupMessage(selectedAgencyId ? `Lead asignado a ${agencyName}` : "Agencia removida del lead");
      setIsPopupError(false);
      setShowSuccessPopup(true);
      setIsAgencyModalOpen(false);
      setRefresh((prev) => prev + 1);
    } catch (error) {
      console.error("Error asignando agencia:", error);
      setPopupMessage(
        error.response?.data?.message || "No se pudo asignar la agencia"
      );
      setIsPopupError(true);
      setShowSuccessPopup(true);
    } finally {
      setIsAssigningAgency(false);
    }
  };

  // Set page context
  React.useEffect(() => {
    if (currentUser) {
      setPageContext({
        title: headerInfo.title,
        subtitle: headerInfo.subtitle,
        showTitle: true,
        rightContent: null,
        breadcrumbItems: [{ label: "Vista Global" }],
      });
    }
  }, [
    currentUser,
    setPageContext,
    headerInfo.title,
    headerInfo.subtitle,
  ]);

  // Mapear leads (sin paginación del cliente, ya viene paginado del servidor)
  const leads = React.useMemo(() => {
    if (!allLeads) return [];

    return allLeads.map((lead) => ({
      id: lead.id,
      name: lead.name || "Sin nombre",
      email: lead.email,
      phone: lead.phone || "-",
      company: lead.company || "-",
      website: lead.website || null,
      status: lead.status,
      status_display: lead.status_display,
      strategy_uuid: lead.strategy_uuid,
      agency_id: lead.agency_id || null,
      agency_name: lead.agency_name || null,
      created_at: lead.created_at,
      updated_at: lead.updated_at,
    }));
  }, [allLeads]);

  const leadColumns = [
    {
      key: "company",
      header: "Empresa",
      minWidth: { xs: "100px", md: "150px" },
      render: (lead) => (
        <Tooltip title={lead.company} arrow>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <Building2
              size={14}
              color={theme.palette.text.secondary}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: { xs: "0.75rem", md: "0.875rem" },
              }}
            >
              {lead.company}
            </Typography>
          </Box>
        </Tooltip>

      ),
    },
    {
      key: "name",
      header: "Nombre",
      minWidth: { xs: "120px", md: "150px" },
      render: (lead) => (
        <Tooltip title={lead.name} arrow>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <User
              size={14}
              color={theme.palette.text.secondary}
            />
            <Typography
              variant="body2"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: { xs: "0.75rem", md: "0.875rem" },
              }}
            >
              {lead.name}
            </Typography>
          </Box>
        </Tooltip>

      ),
    },
    {
      key: "contact",
      header: "Contacto",
      minWidth: { xs: "180px", md: "220px" },
      render: (lead) => (
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          <Box
            component="a"
            href={`mailto:${lead.email}`}
            onClick={(event) => event.stopPropagation()}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              color: theme.palette.text.primary,
              textDecoration: "none",
              fontSize: { xs: "0.7rem", md: "0.75rem" },
              transition: "color 0.2s ease-in-out",
              minWidth: 0,
              "&:hover": {
                color: theme.palette.primary.main,
                textDecoration: "underline",
              },
            }}
          >
            <Mail size={12} color={theme.palette.text.secondary} />
            <Typography
              component="span"
              variant="caption"
              sx={{
                fontSize: "inherit",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {lead.email}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <Phone
              size={12}
              color={theme.palette.text.secondary}
            />
            <Typography
              variant="caption"
              color="text.primary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: { xs: "0.7rem", md: "0.75rem" },
              }}
            >
              {lead.phone}
            </Typography>
          </Box>
        </Stack>
      ),
    },
    {
      key: "website",
      header: "Web",
      minWidth: { xs: "100px", md: "150px" },
      render: (lead) =>
        lead.website ? (
          <Tooltip title={lead.website} arrow>
            <Box
              component="a"
              href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                color: "#1976d2",
                textDecoration: "none",
                fontSize: { xs: "0.7rem", md: "0.75rem" },
                transition: "color 0.2s ease-in-out",
                maxWidth: "150px",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              <Box sx={{ color: theme.palette.text.secondary, display: "flex" }}>
                <Globe size={12} />
              </Box>
              <Typography
                component="span"
                variant="caption"
                sx={{
                  fontSize: "inherit",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {lead.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
              </Typography>
            </Box>
          </Tooltip>
        ) : (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
          >
            -
          </Typography>
        ),
    },
    {
      key: "agency",
      header: "Agencia",
      minWidth: { xs: "100px", md: "150px" },
      render: (lead) =>
        lead.agency_name ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <AgencyIcon size={19} color={theme.palette.text.secondary} />
            <Typography
              variant="body2"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: { xs: "0.75rem", md: "0.875rem" },
              }}
            >
              {lead.agency_name}
            </Typography>
          </Box>
        ) : (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: { xs: "0.7rem", md: "0.75rem" } }}
          >
            -
          </Typography>
        ),
    },
    {
      key: "status",
      header: "Estado",
      minWidth: { xs: "80px", md: "100px" },
      render: (lead) => {
        const statusInfo = statusConfig[lead.status] || statusConfig.new;
        return (
          <Badge variant="outline" color={statusInfo.color}>
            {statusInfo.label}
          </Badge>

        );
      },
    },
    {
      key: "created",
      header: "Fecha",
      minWidth: { xs: "90px", md: "120px" },
      render: (lead) => (
        <Tooltip
          title={new Date(lead.created_at).toLocaleDateString(
            "es-ES",
            {
              day: "numeric",
              month: "long",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            }
          )}
          arrow
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <Clock
              size={12}
              color={theme.palette.text.secondary}
            />
            <Typography
              variant="caption"
              color="text.primary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: { xs: "0.7rem", md: "0.75rem" },
              }}
            >
              {new Date(lead.created_at).toLocaleDateString(
                "es-ES",
                {
                  day: "numeric",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                }
              )}
            </Typography>
          </Box>
        </Tooltip>

      ),
    },
    {
      key: "actions",
      header: "",
      minWidth: { xs: "40px", md: "50px" },
      align: "center",
      width: "50px",
      render: (lead) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <IconButton
            size="small"
            onClick={(e) => handleMenuOpen(e, lead)}
            sx={{
              width: "32px",
              height: "32px",
              padding: 0,
              flexShrink: 0,
            }}
          >
            {isProcessing === lead.id ? (
              <CircularProgress size={16} />
            ) : (
              <MoreVertical size={16} />
            )}
          </IconButton>
          {menuOpen && selectedLeadForMenu?.id === lead.id && (
            <DropdownMenuContent
              anchorEl={menuAnchorEl}
              open={menuOpen}
              onClose={handleMenuClose}
              align="end"
              onClick={(e) => e.stopPropagation()}
            >
              {lead.status !== "new" && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    updateLeadStatus(lead.id, "new");
                  }}
                  disabled={isProcessing === lead.id}
                >
                  <Circle size={16} fill="currentColor" />
                  Lead Nuevo
                </DropdownMenuItem>
              )}
              {lead.status !== "contacted" && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    updateLeadStatus(lead.id, "contacted");
                  }}
                  disabled={isProcessing === lead.id}
                >
                  <MessageSquare size={16} />
                  Lead Contactado
                </DropdownMenuItem>
              )}
              {lead.status !== "converted" && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    updateLeadStatus(lead.id, "converted");
                  }}
                  disabled={isProcessing === lead.id}
                >
                  <CheckCircle size={16} />
                  Lead Convertido
                </DropdownMenuItem>
              )}
              {/* Ver Estrategia */}
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(
                    `/HerramientasIA/planes/${lead.strategy_uuid}?hideForm=true`
                  );
                  handleMenuClose();
                }}
              >
                <FileText size={16} />
                Ver Estrategia
              </DropdownMenuItem>
              {currentUser?.role === "superadmin" && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenAgencyModal();
                  }}
                  disabled={isProcessing === lead.id}
                >
                  <AgencyIcon size={16} color="currentColor" />
                  Asociar Agencia
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          )}
        </Box>

      ),
    },
  ];

  return (
    <Stack
      spacing={1.5}
      sx={{ width: "100%", maxWidth: "100%", overflow: "visible" }}
    >
      <DataTable
        columns={leadColumns}
        rows={leads}
        loading={isLoadingLeads}
        emptyIcon={Users}
        emptyMessage={
          searchTerm
            ? "No se encontraron leads con los filtros"
            : "No hay leads disponibles"
        }
        tableMinWidth={350}
        maxHeight="calc(100vh - 295px)"
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder="Buscar leads..."
        tabs={[
          { value: "all", label: "Todos" },
          { value: "new", label: "Nuevos" },
          { value: "contacted", label: "Contactados" },
          { value: "converted", label: "Convertidos" },
        ]}
        activeTab={selectedStatus}
        onTabChange={handleStatusChange}
        page={currentPage}
        rowsPerPage={pageSize}
        totalCount={totalCount}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* Modal para asociar agencia */}
      <UnifiedModal
        open={isAgencyModalOpen}
        onClose={() => setIsAgencyModalOpen(false)}
        title="Asociar Agencia"
        maxWidth="sm"
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Selecciona la agencia a la que deseas asociar este lead:
          </Typography>
          <CustomSelector
            value={selectedAgencyId}
            onChange={setSelectedAgencyId}
            options={agencyOptions}
            placeholder="Seleccionar agencia..."
            searchable
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
            <Button
              variant="ghost"
              onClick={() => setIsAgencyModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleAssignAgency}
              disabled={selectedAgencyId === undefined || isAssigningAgency}
            >
              {isAssigningAgency ? "Asignando..." : "Asignar"}
            </Button>
          </Box>
        </Box>
      </UnifiedModal>

      {/* Popup de éxito/error */}
      <SuccessPopup
        isOpen={showSuccessPopup}
        onClose={() => setShowSuccessPopup(false)}
        message={popupMessage}
        error={isPopupError}
      />
    </Stack >
  );
}
